import { useParams } from 'react-router-dom';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import useFetchEventOverviewStats, { EStatKeys } from '../helpers/use-fetch-event-overview-stats';
import IconAttendeeGradient from '@ui/icons/gradient-icons/icon-attendee-gradient';
import IconStarGradient from '@ui/icons/gradient-icons/icon-star-gradient';
import IconPlayGradient from '@ui/icons/gradient-icons/icon-play-gradient';
import { getPublishedStatus } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import { EProjectStatus } from '@/types/projects.types';
import StatsCard from '../components/stats-card';
import Link from '@ui/links/link';
import { useIsLoadingStat } from '../helpers/use-is-loading-stat';
import { formatMinutesToHoursAndMinutes } from '@/utils/dates';
import ViewStatsContent from '../components/view-stats-content';
import useGetStatsIconColors from '../helpers/use-get-stats-icon-colors';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import { EPermissions } from '@/types/permissions.types';
// icon for "Likes" feature
// import IconTouchAppGradient from '@ui/icons/gradient-icons/icon-touch-app-gradient';

const TownhallStats = () => {
  const params = useParams();
  const language = params?.language || 'en';
  const projectUuid = params?.projectUuid || '';

  const { data } = useFetchEventOverviewStats({
    projectUuid,
    language,
    stats: [
      EStatKeys.uniqueAttendees,
      EStatKeys.registrations,
      EStatKeys.avgUserWatchtime,
      EStatKeys.averageProjectRating,
    ],
  });

  const publishedStatus = useAppSelector(getPublishedStatus);

  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const isLoadingStat = useIsLoadingStat(data, isPublished);

  const statsColors = useGetStatsIconColors();

  return (
    <Container padding="0 var(--main-content-horizontal-padding)" data-component="TownhallStats">
      <Container className="margin-b-8 flex ai-center jc-space-between">
        <Text tag="h5" className="H5">
          Stats
        </Text>

        <Container>
          <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
            <Link
              to={`/project/edit/${projectUuid}/${language}/stats`}
              textColor="none"
              focusBackgroundColor="rgba(var(--gray-700-rgb), 0.5)"
              applyFocusStatesToAllChildren
              hoverOpacity={0.8}
            >
              <ViewStatsContent />
            </Link>
          </AuthWrapper>
        </Container>
      </Container>
      <Container className="flex fd-row jc-space-evenly ai-center gap-8">
        <StatsCard
          loading={isLoadingStat(EStatKeys.uniqueAttendees)}
          title="Attendance"
          tooltip="Unique users who have interacted with any session video content across this event."
          value={isPublished ? (data[EStatKeys.uniqueAttendees]?.value ?? -1) : -1}
          subvalue={data[EStatKeys.uniqueAttendees]?.subValue}
          Icon={<IconAttendeeGradient from={statsColors.from} to={statsColors.to} />}
        />
        <StatsCard
          loading={isLoadingStat(EStatKeys.avgUserWatchtime)}
          title="Watchtime"
          tooltip="Average duration of video content consumed by attendee"
          value={isPublished ? formatMinutesToHoursAndMinutes(data.avgUserWatchtime?.value ?? 0) : -1}
          Icon={<IconPlayGradient from={statsColors.from} to={statsColors.to} />}
        />
        <StatsCard
          loading={isLoadingStat(EStatKeys.averageProjectRating)}
          title="Ratings"
          tooltip={'Average combined rating of project sessions.'}
          value={isPublished ? (data[EStatKeys.averageProjectRating]?.value ?? -1) : -1}
          Icon={<IconStarGradient from={statsColors.from} to={statsColors.to} />}
        />
      </Container>
    </Container>
  );
};

export default TownhallStats;
